<template>
  <v-container>
    <v-card class="text-secondary" title="Discover detailed information about the characters of the galaxy. Use the search to explore!" flat>
      <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" variant="outlined" hide-details single-line dense></v-text-field>

      <v-data-table :items="filteredCharacters" :headers="headers" :search="search" :items-per-page="25">
        <template v-slot:loading>
          <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
        </template>

        <!-- Table Rows -->
        <template v-slot:item="props">
          <tr>
            <td>
              <a :href="props.item.uri" target="_blank" class="text-decoration-none">
                {{ props.item.name }}
              </a>
            </td>
            <td>
              <span v-if="props.item.race_URI && props.item.race_URI.trim() !== 'Unknown'">
                <a :href="props.item.race_URI.trim()" target="_blank" class="text-decoration-none">
                  {{ props.item.race }}
                </a>
              </span>
              <span v-else>Unknown</span>
            </td>
            <td>{{ props.item.sex }}</td>
            <td>{{ Number(props.item.height).toLocaleString() + ' ' + props.item.height_unit }}</td>
            <td>{{ Number(props.item.mass).toLocaleString() + ' ' + props.item.mass_unit }}</td>
            <td>
              <span v-if="props.item.homeworld_URI && props.item.homeworld_URI.trim() !== 'Unknown'">
                <a :href="props.item.homeworld_URI.trim()" target="_blank" class="text-decoration-none">
                  {{ props.item.homeworld }}
                </a>
              </span>
              <span v-else>Unknown</span>
            </td>
            <td>
              <div style="max-height: 100px; overflow-y: auto;" class="my-2">
                <div v-for="(occupation_URI, index) in (props.item.occupations_URI ? props.item.occupations_URI.split(';') : [])" :key="index">
                  <a v-if="occupation_URI.trim() !== ''" :href="occupation_URI.trim()" target="_blank" class="text-decoration-none">
                    {{
                      (props.item.occupations && props.item.occupations.split(';')[index]?.trim()) || 'Unknown'
                    }}
                  </a>
                  <span v-else>Unknown</span>
                </div>
              </div>
            </td>
            <td>
              <div style="max-height: 100px; overflow-y: auto;" class="my-2">
                <div v-for="(affiliation_URI, index) in (props.item.affiliations_URI ? props.item.affiliations_URI.split(';') : [])" :key="index">
                  <a v-if="affiliation_URI.trim() !== ''" :href="affiliation_URI.trim()" target="_blank" class="text-decoration-none">
                    {{
                      (props.item.affiliations && props.item.affiliations.split(';')[index]?.trim()) || 'Unknown'
                    }}
                  </a>
                  <span v-else>Unknown</span>
                </div>
              </div>
            </td>
            <td>
              <div style="max-height: 100px; overflow-y: auto;" class="my-2">
                <div v-for="(work_URI, index) in (props.item.works_URI ? props.item.works_URI.split(';') : [])" :key="index">
                  <a v-if="work_URI.trim() !== ''" :href="work_URI.trim()" target="_blank" class="text-decoration-none">
                    {{
                      (props.item.works && props.item.works.split(';')[index]?.trim()) || 'Unknown'
                    }}
                  </a>
                  <span v-else>Unknown</span>
                </div>
              </div>
            </td>
            <td>
              <a :href="props.item.wookiepedia_url" target="_blank" class="text-decoration-none">
                Go to {{ props.item.wookiepedia_url.replace('https://starwars.fandom.com/wiki/', '') }} page
              </a>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import apiClient from '@/apiClient';
import { useHead } from '@unhead/vue';

export default {
  name: "Characters_Search",
  data() {
    return {
      search: '',
      characters: [],
      headers: [
        { title: "Name", value: "name",align: "center",sortable: true},
        { title: "Race", value: "race",align: "center"},
        { title: "Sex", value: "sex",align: "center"},
        { title: "Height", value: "height",align: "center"},
        { title: "Mass", value: "mass",align: "center"},
        { title: "Homeworld", value: "homeworld",align: "center"},
        { title: "Occupations", value: "occupations",align: "center"},
        { title: "Affiliations", value: "affiliations",align: "center"},
        { title: "Works", value: "works",align: "center"},
        { title: "Wookieepedia Page", value: "wookiepedia_url",align: "center"}
      ]
    };
  },
  computed: {
    filteredCharacters() {
      const query = this.search.toLowerCase();
      return this.characters.filter(character => (character.name && character.name.toLowerCase().includes(query)) || (character.works && character.works.toLowerCase().includes(query))
      );
    }
  },
  mounted() {
    // Fetch data from backend
    apiClient.get("/api/characters")
      .then(response => {
        this.characters = response.data;
        console.log("Characters data loaded:", this.characters);
      })
      .catch(error => {
        console.error("Error fetching characters:", error);
      });
  },
  setup() {
    useHead({
      title: "Star Wars Characters – Explore by Occupation, Race & Affiliation",
      meta: [
        {
          name: "description",
          content: "Discover Star Wars characters with detailed filters: search by occupation, race, affiliation, and more. Explore the galaxy’s heroes, villains, and species in the Holonet Galactic Encyclopedia, powered by Wikidata.",
        },
        {
          property: "og:title",
          content: "Star Wars Characters – Explore by Occupation, Race & Affiliation",
        },
        {
          property: "og:description",
          content: "Search and explore Star Wars characters by occupation, race, affiliation, and other attributes. Dive into detailed profiles in this interactive encyclopedia.",
        },
        {
          property: "og:url",
          content: "https://sw-holonet.com/characters/table",
        },
        { property: "og:type", content: "website" },
        {
          name: "twitter:title",
          content: "Star Wars Characters – Explore by Occupation, Race & Affiliation",
        },
        {
          name: "twitter:description",
          content: "Search Star Wars characters by occupation, race, and affiliation. Discover detailed profiles in the Holonet Galactic Encyclopedia.",
        },
        { name: "twitter:card", content: "summary" },
        {
          name: "keywords",
          content: "Star Wars characters, Star Wars heroes, Star Wars villains, Star Wars species, Star Wars occupations, Star Wars affiliations, Wikidata Star Wars, Holonet characters",
        },
      ],
      });
  }
};
</script>

<style>
.planet-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.v-col {
  display: flex;
  align-items: center;
}

a {
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}
</style>
